import type React from 'react'
import { useHistory } from 'react-router-dom'
import ModalWindow from '../ModalWindow/ModalWindow'
import './ProTrialActivationModal.scss'
import CircleCheckIcon from 'components/icons/CircleCheckIcon'

interface ProTrialActivationModalProps {
  isOpened: boolean
  onCloseModal: () => void
}

const ProTrialActivationModal: React.FC<ProTrialActivationModalProps> = ({ isOpened, onCloseModal }) => {
  const history = useHistory()

  const handleGoToGuides = () => {
    onCloseModal()
    if (window && window.open) {
      window.open('/guides', '_blank')
    } else {
      history.push('/guides')
    }
  }

  return (
    <ModalWindow
      isOpened={isOpened}
      isCloseButton={true}
      onCloseModal={onCloseModal}
      closeButtonPlacement={{ top: '15px', right: '15px' }}
      className="pro-trial-activation-modal"
      dataTest="pro-trial-modal"
    >
      <div className="pro-trial-activation-content">
        <div className="pro-trial-card">
          <div className="pro-trial-header">
            <div className="pro-trial-title">Pro Trial Activated — 14 days of full access remaining</div>
          </div>

          <div className="pro-trial-description">Enjoy full access to every Pro feature during your trial!</div>

          <ul className="pro-trial-features">
            <li>
              <span className="check-icon"><CircleCheckIcon width={16} height={16} /></span>
              <span><strong>2 Takeoffs</strong> – measure up to 2 projects during your trial.</span>
            </li>
            <li>
              <span className="check-icon"><CircleCheckIcon width={16} height={16} /></span>
              <span><strong>1-Click Proposals</strong> – instantly turn measurements into client-ready bids.</span>
            </li>
            <li>
              <span className="check-icon"><CircleCheckIcon width={16} height={16} /></span>
              <span><strong>Real-Time Cost Estimation</strong> – see quantities and costs update as you measure.</span>
            </li>
            <li>
              <span className="check-icon"><CircleCheckIcon width={16} height={16} /></span>
              <span><strong>Cloud File & Plan Management</strong> – store and access drawings anywhere.</span>
            </li>
            <li>
              <span className="check-icon"><CircleCheckIcon width={16} height={16} /></span>
              <span><strong>One to Many pricing</strong> – apply multiple cost items to a single measurement.</span>
            </li>
            <li>
              <span className="check-icon"><CircleCheckIcon width={16} height={16} /></span>
              <span><strong>SMART Scopes</strong> – draw once and auto-calculate full material lists for walls, slabs, windows, and more.</span>
            </li>
            <li>
              <span className="check-icon"><CircleCheckIcon width={16} height={16} /></span>
              <span><strong>Export CSV & PDF mark-ups</strong> – export your takeoffs and mark-ups for reporting and sharing.</span>
            </li>
          </ul>

          <div className="ai-section">
            <div className="ai-title">AI Powered Shortcuts</div>
            <ul className="ai-features">
              <li>
                <span className="check-icon"><CircleCheckIcon width={16} height={16} /></span>
                <span><strong>Auto Sheet Naming</strong> – organizes sheets by title block automatically.</span>
              </li>
              <li>
                <span className="check-icon"><CircleCheckIcon width={16} height={16} /></span>
                <span><strong>1-Click Elevation Takeoffs</strong> – measure walls and elevations instantly.</span>
              </li>
              <li>
                <span className="check-icon"><CircleCheckIcon width={16} height={16} /></span>
                <span><strong>Symbol & Item Counts</strong> – auto-counts doors, windows, outlets, and fixtures.</span>
              </li>
              <li>
                <span className="check-icon"><CircleCheckIcon width={16} height={16} /></span>
                <span><strong>AutoScale Sheets</strong> – reads plan notes and calibrates scale automatically.</span>
              </li>
            </ul>
          </div>

          {/* ── Guides CTA ── */}
          <div
            className="pro-trial-guides"
            onClick={handleGoToGuides}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleGoToGuides()
            }}
          >
            <div className="pro-trial-guides__icon">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" />
                <path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" />
              </svg>
            </div>
            <div className="pro-trial-guides__text">
              <div className="pro-trial-guides__title">New to TakeCost? Check out our guides</div>
              <div className="pro-trial-guides__desc">
                Step-by-step tutorials for every tool — area, counter, SMART assemblies, and more.
              </div>
            </div>
            <svg
              className="pro-trial-guides__arrow"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
          </div>

          <div className="pro-trial-footer">Trial ends in 14 days — then you're on Free tier with 1 Takeoff each month.</div>
        </div>
      </div>
    </ModalWindow>
  )
}

export default ProTrialActivationModal
